import { B, fonts, Btn, Badge } from "../../../components/UI";
import CustomerDetailPage from "./CustomerDetailPage";

// Customers tab isolates customer list + CRUD interactions for easier debugging.
export default function CustomersTab({
  customers,
  filteredCustomers,
  paginatedCustomers,
  permissions,
  customerSearch,
  setCustomerSearch,
  customerPage,
  customerPageSize,
  customerTotalPages,
  setCustomerPage,
  showCustomerForm,
  setShowCustomerForm,
  editCustomer,
  setEditCustomer,
  viewCustomer,
  setViewCustomer,
  handleSaveCustomer,
  customerSaving,
  handleDeleteCustomer,
  CustomerForm,
  transports = [],
  designs,
  showToast,
  showFirmName = true,
}) {
  if (viewCustomer && !editCustomer && !showCustomerForm) {
    return (
      <CustomerDetailPage
        customer={viewCustomer}
        designs={designs}
        showFirmName={showFirmName}
        onBack={() => setViewCustomer(null)}
        onEdit={() => { setEditCustomer(viewCustomer); setViewCustomer(null); setShowCustomerForm(false); }}
      />
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
        <p style={{ margin: 0, fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>{customers.length} total customers</p>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input
            value={customerSearch}
            onChange={e => setCustomerSearch(e.target.value)}
            placeholder={showFirmName ? "Search name, mobile, city, firm, agent, GST..." : "Search name, mobile, city, agent, GST..."}
            style={{ background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 13px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none", width: 240 }}
          />
          {permissions?.canManageCustomers && !showCustomerForm && !editCustomer && (
            <Btn onClick={() => setShowCustomerForm(true)} variant="primary">+ Add Customer</Btn>
          )}
        </div>
      </div>

      {(showCustomerForm || editCustomer) && (
        <CustomerForm
          initial={editCustomer || viewCustomer}
          onDone={() => { setShowCustomerForm(false); setEditCustomer(null); setViewCustomer(null); }}
          onSubmit={handleSaveCustomer}
          saving={customerSaving}
          readOnly={Boolean(viewCustomer)}
          showToast={showToast}
          showFirmName={showFirmName}
          transports={transports}
        />
      )}

      <div style={{ overflowX: "auto", background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1080 }}>
          <thead>
            <tr style={{ background: B.orangeFaint }}>
              {["Customer Name", ...(showFirmName ? ["Firm Name"] : []), "Agent Name", "GST Number", "City", "Pincode", "Mobile Number", "Status", "Actions"].map(h => (
                <th key={h} style={{ textAlign: "left", fontSize: 11, color: B.textMid, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "12px 10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedCustomers.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ textAlign: "center", padding: "24px", color: B.textLight, fontFamily: fonts.body }}>
                  No customers found.
                </td>
              </tr>
            ) : paginatedCustomers.map(customer => (
              <tr key={customer.id}>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{customer.customer_name || "—"}</td>
                {showFirmName && <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{customer.firm_name || "—"}</td>}
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{customer.agent_name || "—"}</td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{customer.gst_number || "—"}</td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{customer.city || "—"}</td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{customer.pincode || "—"}</td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{customer.mobile_number || "—"}</td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>
                  <Badge color={customer.status === "active" ? B.green : customer.status === "inactive" ? B.gold : B.red}>{customer.status || "active"}</Badge>
                </td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button type="button" onClick={() => { setViewCustomer(customer); setShowCustomerForm(false); setEditCustomer(null); }} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 16 }} title="View">👁️</button>
                    <button type="button" onClick={() => { if (!permissions?.canManageCustomers) return; setEditCustomer(customer); setShowCustomerForm(false); setViewCustomer(null); }} disabled={!permissions?.canManageCustomers} style={{ border: "none", background: "transparent", cursor: permissions?.canManageCustomers ? "pointer" : "not-allowed", fontSize: 16, opacity: permissions?.canManageCustomers ? 1 : 0.4 }} title="Edit">✏️</button>
                    <button type="button" onClick={() => permissions?.canManageCustomers && handleDeleteCustomer(customer)} disabled={!permissions?.canManageCustomers || customer.status === "deleted"} style={{ border: "none", background: "transparent", cursor: permissions?.canManageCustomers && customer.status !== "deleted" ? "pointer" : "not-allowed", fontSize: 16, opacity: permissions?.canManageCustomers && customer.status !== "deleted" ? 1 : 0.4 }} title="Delete">🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredCustomers.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>
            Showing {(customerPage - 1) * customerPageSize + 1}-{Math.min(customerPage * customerPageSize, filteredCustomers.length)} of {filteredCustomers.length}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={() => setCustomerPage(p => Math.max(1, p - 1))} variant="ghost" small disabled={customerPage === 1}>Previous</Btn>
            <span style={{ fontSize: 12, color: B.textMid, alignSelf: "center", fontFamily: fonts.body }}>Page {customerPage} / {customerTotalPages}</span>
            <Btn onClick={() => setCustomerPage(p => Math.min(customerTotalPages, p + 1))} variant="ghost" small disabled={customerPage === customerTotalPages}>Next</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
